import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Target, 
  Eye, 
  Heart, 
  Award, 
  Handshake, 
  Lightbulb,
  Globe2,
  TrendingUp,
  ArrowUpRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

export const About = () => {
  const { t } = useLanguage();

  const values = [
    { icon: Heart, title: t('about.values.integrity'), description: t('about.values.integrity_desc') },
    { icon: Award, title: t('about.values.quality'), description: t('about.values.quality_desc') },
    { icon: Handshake, title: t('about.values.partnership'), description: t('about.values.partnership_desc') },
    { icon: Lightbulb, title: t('about.values.innovation'), description: t('about.values.innovation_desc') },
  ];

  const timeline = [
    { year: '2010', title: 'Founded', description: 'Started as a small grain trading company' },
    { year: '2014', title: 'Global Expansion', description: 'Expanded operations to 20+ countries' },
    { year: '2018', title: 'Diversification', description: 'Added machinery and fertilizers to portfolio' },
    { year: '2022', title: 'Digital Platform', description: 'Launched online trading platform' },
    { year: '2024', title: '1000+ Partners', description: 'Reached milestone of 1000 global partners' },
  ];

  return (
    <div className="min-h-screen bg-[#f9f9f7]" data-testid="about-page">
      {/* Hero - Premium */}
      <section className="relative overflow-hidden pt-20 sm:pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d1d35] to-[#0a1628]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-2/3 h-full" style={{
            backgroundImage: `radial-gradient(circle at 60% 40%, rgba(197, 160, 89, 0.3) 0%, transparent 50%)`
          }} />
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center py-12 sm:py-16 md:py-24 lg:py-32">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-flex items-center gap-3 text-secondary-500 mb-4 sm:mb-6">
                <span className="w-12 h-px bg-secondary-500" />
                <span className="text-xs font-mono uppercase tracking-[0.3em]">{t('about.story.title')}</span>
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight leading-tight mb-6">
                {t('about.title')}
              </h1>
              <p className="text-xl text-white/60 leading-relaxed">
                {t('about.subtitle')}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="aspect-[4/3] overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070"
                  alt="Global trade"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Stats overlay - Premium */}
              <div className="absolute -bottom-8 -left-8 bg-gradient-to-br from-secondary-500 to-secondary-600 p-8 shadow-2xl">
                <p className="text-4xl font-heading font-bold text-primary-900 mb-1">$500M+</p>
                <p className="text-sm text-primary-900/70">Annual Trade Volume</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story & Mission */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 bg-white border border-border p-10 md:p-14"
            >
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-secondary-500 mb-4">
                {t('about.story.title')}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('about.story.content')}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-4">
                Today, we connect thousands of farmers, manufacturers, and buyers across continents, ensuring quality products reach destinations worldwide with unmatched reliability and service.
              </p>
            </motion.div>

            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-[#0a1628] text-white p-8"
              >
                <Target className="w-8 h-8 text-secondary-500 mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-heading font-bold mb-3">{t('about.mission.title')}</h3>
                <p className="text-white/70 leading-relaxed text-sm">
                  {t('about.mission.content')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-secondary-500 text-primary-900 p-8"
              >
                <Eye className="w-8 h-8 mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-heading font-bold mb-3">{t('about.vision.title')}</h3>
                <p className="text-primary-900/70 leading-relaxed text-sm">
                  {t('about.vision.content')}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Values - Premium */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-xl mb-16"
          >
            <span className="inline-flex items-center gap-3 text-secondary-500 mb-4">
              <span className="w-12 h-px bg-secondary-500" />
              <span className="text-xs font-mono uppercase tracking-[0.3em]">What Drives Us</span>
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary-900 tracking-tight">
              {t('about.values.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-[#f9f9f7] border border-border p-8 hover:bg-[#0a1628] hover:border-transparent transition-all duration-500"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 bg-secondary-500/10 border border-secondary-500/30 flex items-center justify-center mb-6 group-hover:bg-secondary-500/20 group-hover:border-secondary-500/50 transition-all duration-500">
                  <value.icon className="w-6 h-6 text-secondary-500" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-heading font-bold text-primary-900 group-hover:text-white mb-3 transition-colors duration-300">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground group-hover:text-white/70 transition-colors duration-300 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-secondary-500 mb-4">
              Our Journey
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary-900 tracking-tight">
              15 Years of Growth
            </h2>
          </motion.div>

          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-0 md:left-1/2 w-4 h-4 bg-[#0a1628] md:-translate-x-1/2 z-10" />
                  
                  {/* Content */}
                  <div className={`ml-8 md:ml-0 md:w-1/2 ${
                    index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'
                  }`}>
                    <span className="text-xs font-mono uppercase tracking-widest text-secondary-500">{item.year}</span>
                    <h3 className="text-xl font-heading font-bold text-primary-900 mt-2">{item.title}</h3>
                    <p className="text-muted-foreground mt-1">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-24 md:py-32 bg-[#0a1628] relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-secondary-400 mb-6">
                Worldwide Network
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight mb-8">
                Global Presence,<br />Local Expertise
              </h2>
              <p className="text-lg text-white/60 leading-relaxed mb-10">
                With operations spanning across continents, we bring together the best of global trade with deep local market knowledge. Our network ensures seamless transactions and reliable supply chains.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <Globe2 className="w-8 h-8 text-secondary-500 mb-4" strokeWidth={1.5} />
                  <p className="text-4xl font-heading font-bold text-white">50+</p>
                  <p className="text-sm text-white/50 mt-1">Countries</p>
                </div>
                <div>
                  <TrendingUp className="w-8 h-8 text-secondary-500 mb-4" strokeWidth={1.5} />
                  <p className="text-4xl font-heading font-bold text-white">$500M+</p>
                  <p className="text-sm text-white/50 mt-1">Trade Volume</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/2933243/pexels-photo-2933243.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Agricultural machinery"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-secondary-500 p-12 md:p-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-900 mb-2">
                Ready to partner with us?
              </h2>
              <p className="text-primary-900/70">
                Get in touch to discuss your commodity requirements.
              </p>
            </div>
            <Link to="/contact">
              <Button className="bg-[#0a1628] hover:bg-primary-800 text-white rounded-none px-10 py-6 text-xs font-bold uppercase tracking-widest group whitespace-nowrap">
                Contact Us
                <ArrowUpRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
